import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { newGoal, pluralLabel, type SpreadPreset } from '../../model/goals';
import { SERVER_SUPPORTS } from '../../model/serverSupport';
import type { Column } from '../../model/types';
import { useSetup } from '../../SetupContext';
import { ChoiceButton } from './ChoiceButton';
import { columnTypeText, MULTI_CHOICE_NOTE } from './columnText';
import classes from './GoalsStep.module.css';

interface AddGoalModalProps {
  onClose: () => void;
  /** Called with the new goal's column and which sections to open first. */
  onCreated: (column: string, openSections: SpreadPreset[]) => void;
}

/** Three steps: pick a column → same or spread → (spread) which limits. */
export function AddGoalModal({ onClose, onCreated }: AddGoalModalProps) {
  const { session, config, update, values } = useSetup();
  const [column, setColumn] = useState<Column | null>(null);
  const [spreading, setSpreading] = useState(false);
  const [presets, setPresets] = useState<SpreadPreset[]>([]);

  const goalCols = new Set(config.goals.map(g => g.column));
  const ruleCols = new Set(config.columnRules.map(r => r.column));
  const options = session.columns.filter(c => !goalCols.has(c.key));
  const label = column?.label.toLowerCase() ?? 'value';
  const isMulti = column?.kind === 'multi';

  const create = (
    how: 'keep_together' | 'spread',
    open: SpreadPreset[] = []
  ) => {
    if (!column) return;
    update({
      type: 'addGoal',
      goal: newGoal(column, how, (values[column.key] ?? []).length, open),
    });
    onCreated(column.key, open);
    onClose();
  };

  const spreadChoices: {
    id: SpreadPreset;
    label: string;
    description: string;
  }[] = [
    {
      id: 'types',
      label: 'Limit types in each group',
      description: SERVER_SUPPORTS.typesMax
        ? `Set the fewest and most ${pluralLabel(label)} a group can have.`
        : `Set the fewest ${pluralLabel(label)} a group can have.`,
    },
    {
      id: 'buddy',
      label: 'Give everyone a buddy',
      description: `Make sure a group never has just one person of any ${label}.`,
    },
    {
      id: 'limits',
      label: 'Limit people of each type',
      description: `Set the fewest and most people of each ${label} a group can have, if that ${label} is in the group.`,
    },
  ];

  return (
    <Modal
      opened
      onClose={onClose}
      title="Add a matching goal"
      size={560}
      centered
    >
      {!column && (
        <Stack gap="xs">
          <Text size="sm" c="dimmed" mb="xs">
            Pick a column. Any column can be a matching goal.
          </Text>
          {options.length === 0 && (
            <Paper bg="gray.0" p="md" radius="md">
              <Text size="sm">Every column is already a matching goal.</Text>
            </Paper>
          )}
          {options.map(c => (
            <ChoiceButton
              key={c.key}
              label={c.label}
              description={`${columnTypeText(c)}${ruleCols.has(c.key) ? '. This column is also a group requirement.' : ''}`}
              onClick={() => setColumn(c)}
            />
          ))}
        </Stack>
      )}

      {column && !spreading && (
        <Stack gap="sm">
          <Title order={4}>
            How should {column.label} show up across groups?
          </Title>
          <ChoiceButton
            label="Same in each group (keep together)"
            disabled={isMulti}
            description={
              isMulti
                ? MULTI_CHOICE_NOTE
                : `Everyone in a group has the same value. For example, everyone from the same ${label}.`
            }
            onClick={() => create('keep_together')}
          />
          <ChoiceButton
            label="Spread out"
            description="Mix different values across groups. You'll choose how next."
            onClick={() => setSpreading(true)}
          />
          <Button
            variant="default"
            mt="xs"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setColumn(null)}
          >
            Back
          </Button>
        </Stack>
      )}

      {column && spreading && (
        <Stack gap="sm">
          <Box>
            <Title order={4}>How should {column.label} spread out?</Title>
            <Text size="sm" c="dimmed">
              Pick one or more.
            </Text>
          </Box>
          {spreadChoices.map(choice => {
            const on = presets.includes(choice.id);
            const toggle = () =>
              setPresets(
                on
                  ? presets.filter(p => p !== choice.id)
                  : [...presets, choice.id]
              );
            return (
              <UnstyledButton
                key={choice.id}
                role="checkbox"
                aria-checked={on}
                className={classes.checkCard}
                data-checked={on || undefined}
                onClick={toggle}
              >
                <Checkbox
                  checked={on}
                  readOnly
                  tabIndex={-1}
                  aria-hidden
                  mt={2}
                />
                <Box>
                  <Text fw={600}>{choice.label}</Text>
                  <Text size="sm" c="dimmed" mt={2}>
                    {choice.description}
                  </Text>
                </Box>
              </UnstyledButton>
            );
          })}
          <Alert color="blue" variant="light">
            You'll set the specifics for each of these in the next step.
          </Alert>
          <Group justify="space-between" mt="xs">
            <Button variant="default" onClick={() => setSpreading(false)}>
              Back
            </Button>
            <Button
              disabled={presets.length === 0}
              onClick={() => create('spread', presets)}
            >
              Create matching goal
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
